import React, { useState } from 'react';
import { Box, Typography, Paper, Checkbox, Button, styled, Grid, useMediaQuery, useTheme, IconButton, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Vehicle } from '../../state/types';
import SyncTwoToneIcon from '@mui/icons-material/SyncTwoTone';
import Vehicle360Modal from './Vehicle360Modal';
import { useVehiclesContext } from '../../context/VehiclesContext';
import { useVehicleTableLogic } from '../../hooks/vehicles/useVehicleTableLogic';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadFilesAsync } from '../../services/FileDownloader';

const VehicleCardList: React.FC = () => {
  const navigate = useNavigate();
  const { vehicles } = useVehiclesContext();

  const {
    openModal,
    modalVehicle,
    thumbnailErrors,
    handleThumbnailError,
    handle360Click,
    handleCloseModal,
    handleCheckboxClick,
    isSelected,
  } = useVehicleTableLogic();

  const handleRowClick = (clientId: string, assetId: string) => {
    navigate(`/vehicle/${clientId}/${assetId}`);
  };

  const getSyndicationStep = (vehicle: Vehicle) => {
    if (!vehicle || !vehicle.syndicationSteps || !vehicle.syndicationCurrentStep) return ''

    if (vehicle.syndicationCurrentStep === "COMPLETE") return vehicle?.syndicationSteps[0]

    return vehicle.syndicationCurrentStep
  }

  const StatusTag = styled('span')(({ theme, status }: { theme?: any; status: string; }) => {
    let bgColor = theme.palette.grey[500]; // Default for NOT STARTED

    switch (status) {
      case 'COMPLETE': bgColor = theme.palette.success.main; break;
      case 'ERROR': bgColor = theme.palette.error.main; break;
      case 'PENDING': bgColor = theme.palette.warning.main; break;
      case 'NOT STARTED': bgColor = theme.palette.grey[400]; break;
      default: bgColor = theme.palette.grey[500];
    }

    return {
      backgroundColor: bgColor,
      color: theme.palette.getContrastText(bgColor),
      borderRadius: '3px',
      padding: '2px 6px',
      display: 'inline-block',
      fontSize: '11px',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
    };
  }
  );

  const SpinningButton = styled(Button)(({ theme }) => ({
    borderRadius: '5px',
    padding: '12px',
    color: theme.palette.common.black,
    backgroundColor: theme.palette.warning.main,
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 2px 4px ${theme.palette.grey[500]}`,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  }));

  return (
    <>
      {vehicles.map(vehicle => (
        <Paper key={vehicle.assetId} sx={{ mb: 2, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* --- CARD HEADER (No Changes) --- */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {/* ... VIN and Checkbox */}
            <Box display="flex" alignItems="center">
              <Checkbox
                onClick={(e) => handleCheckboxClick(e, vehicle)}
                checked={isSelected(vehicle)}
                size="small"
                sx={{ '&.Mui-checked': { color: "darkred" }, p: 0, mr: 1 }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 500, wordBreak: 'break-word', userSelect: 'text', cursor: 'text' }} onClick={(e) => e.stopPropagation()}>
                {vehicle.vin}
              </Typography>
            </Box>
            {/* ... Action Icons */}
            <Box display="flex" alignItems="center" onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Copy VIN"><IconButton size="small" onClick={() => navigator.clipboard.writeText(vehicle.vin)}><ContentCopyIcon fontSize="inherit" /></IconButton></Tooltip>
              <Tooltip title="Open in New Tab"><IconButton size="small" component="a" href={`/vehicle/${vehicle.clientId}/${vehicle.assetId}`} target="_blank" rel="noopener noreferrer"><OpenInNewIcon fontSize="inherit" /></IconButton></Tooltip>
              <Tooltip title="Download Images"><IconButton size="small" onClick={() => downloadFilesAsync(vehicle)}><DownloadIcon fontSize="inherit" /></IconButton></Tooltip>
            </Box>
          </Box>

          {/* --- THUMBNAIL (No Changes) --- */}
          <Box onClick={() => handleRowClick(vehicle.clientId, vehicle.assetId)} sx={{ cursor: 'pointer', position: 'relative', width: '100%', paddingTop: '56.25%' }}>
            {vehicle.thumbnail && !thumbnailErrors[vehicle.assetId] ? (
              <Box component="img" src={vehicle.thumbnail} onError={() => handleThumbnailError(vehicle.assetId)} sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }} />
            ) : (
              <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                <Typography>No Thumbnail</Typography>
              </Box>
            )}
          </Box>

          {/* --- CARD BODY (Updated Grid Layout) --- */}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* Left Column: ALL Image Counts */}
            <Grid item xs={6}>
              <Box display="flex" flexDirection="column">
                {[
                  { label: "Exterior", count: vehicle.imageExterior?.length ?? 0 },
                  { label: "Cropped", count: vehicle.imageCrop?.length ?? 0 },
                  { label: "Featured", count: vehicle.imageFeatured?.length ?? 0 },
                  { label: "Manual", count: vehicle.imageManual?.length ?? 0 },
                  { label: "Pano", count: vehicle.imagePano?.length ?? 0 },
                  { label: "UC", count: vehicle.imageUndercarriage?.length ?? 0 },
                  { label: "BGR", count: vehicle.imageBgr?.length ?? 0 },
                ].map(({ label, count }) => (
                  <Box key={label} display="flex" justifyContent="space-between">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}:</Typography>
                    <Typography variant="body2">{count}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Right Column: Status & Meta */}
            <Grid item xs={6}>
              <Box display="flex" flexDirection="column" gap={.5} alignItems="flex-start">
                {/* 👇 FIX #2: Stack the status tags vertically to prevent overflow */}
                <Box display="flex" flexDirection="column" alignItems="flex-start">
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>Processing:</Typography>
                  <StatusTag status={vehicle.processingStatus || ''} title={vehicle.processingError || undefined}>
                    {vehicle.processingCurrentStep}
                  </StatusTag>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="flex-start">
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>Syndication:</Typography>
                  <StatusTag status={vehicle.syndicationStatus || ''} title={vehicle.syndicationError || undefined}>
                    {getSyndicationStep(vehicle)}
                  </StatusTag>
                </Box>

                <Typography variant="caption" sx={{ mt: .5 }}>
                  Posted: {new Date(+vehicle.created_at * 1000).toLocaleString()}
                </Typography>
                <Typography variant="caption">Config: {vehicle.config}</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* 👇 FIX #1: Move the 360 logic OUTSIDE and AFTER the grid */}
          <Box sx={{ mt: 1, width: '100%' }}>
            {vehicle.imagePano?.length && (
              <SpinningButton
                fullWidth // Make the button span the full width of the card
                onClick={(e) => handle360Click(e, vehicle.clientId, vehicle.assetId)}
              >
                360 <SyncTwoToneIcon sx={{ fontSize: '14px', ml: 1 }} />
              </SpinningButton>
            )}
          </Box>
        </Paper>
      ))}

      <Vehicle360Modal
        open={openModal}
        handleClose={handleCloseModal}
        clientId={modalVehicle?.clientId || null}
        assetId={modalVehicle?.assetId || null}
      />
    </>
  );
};


export default VehicleCardList;
